import { useMemo, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseValue(value) {
  if (!value || typeof value !== "string") return { day: "", month: "", year: "" };
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return { day: "", month: "", year: "" };
  return {
    day: String(d),
    month: String(m),
    year: String(y),
  };
}

function buildDate(day, month, year) {
  if (!day || !month || !year) return "";
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return "";
  const paddedMonth = String(m).padStart(2, "0");
  const paddedDay = String(d).padStart(2, "0");
  return `${y}-${paddedMonth}-${paddedDay}`;
}

const currentYear = new Date().getFullYear();
const minYear = currentYear - 100;
const maxYear = currentYear - 16;

export default function DobInput({ value, onChange, id = "dob_input", "aria-describedby": ariaDescribedby }) {
  const parsed = useMemo(() => parseValue(value), [value]);
  const [day, setDay] = useState(parsed.day);
  const [month, setMonth] = useState(parsed.month);
  const [year, setYear] = useState(parsed.year);

  useEffect(() => {
    setDay(parsed.day);
    setMonth(parsed.month);
    setYear(parsed.year);
  }, [parsed.day, parsed.month, parsed.year]);

  const years = useMemo(() => {
    const list = [];
    for (let y = maxYear; y >= minYear; y--) list.push(y);
    return list;
  }, []);

  const daysInMonth = useMemo(() => {
    if (!month || !year) return 31;
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    return new Date(y, m, 0).getDate();
  }, [month, year]);

  const days = useMemo(() => {
    const list = [];
    for (let d = 1; d <= daysInMonth; d++) list.push(d);
    return list;
  }, [daysInMonth]);

  const handleChange = (part, val) => {
    let newDay = day;
    let newMonth = month;
    let newYear = year;
    if (part === "day") newDay = val;
    if (part === "month") newMonth = val;
    if (part === "year") newYear = val;
    const maxDay = newMonth && newYear
      ? new Date(parseInt(newYear, 10), parseInt(newMonth, 10), 0).getDate()
      : 31;
    if (newDay && parseInt(newDay, 10) > maxDay) newDay = String(maxDay);
    setDay(newDay);
    setMonth(newMonth);
    setYear(newYear);
    const next = buildDate(newDay, newMonth, newYear);
    if (next) onChange(next);
  };

  return (
    <Grid
      container
      spacing={1}
      role="group"
      aria-describedby={ariaDescribedby || undefined}
      sx={{ mb: 1 }}
    >
      <Grid item xs={12} sm={4}>
        <TextField
          select
          id={id}
          label="Day"
          value={day}
          onChange={(e) => handleChange("day", e.target.value)}
          inputProps={{ "aria-label": "Day", "data-dob-part": "day" }}
        >
          <MenuItem value="">Day</MenuItem>
          {days.map((d) => (
            <MenuItem key={d} value={String(d)}>
              {d}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          label="Month"
          value={month}
          onChange={(e) => handleChange("month", e.target.value)}
          inputProps={{ "aria-label": "Month", "data-dob-part": "month" }}
        >
          <MenuItem value="">Month</MenuItem>
          {MONTHS.map((name, i) => (
            <MenuItem key={name} value={String(i + 1)}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          label="Year"
          value={year}
          onChange={(e) => handleChange("year", e.target.value)}
          inputProps={{ "aria-label": "Year", "data-dob-part": "year" }}
        >
          <MenuItem value="">Year</MenuItem>
          {years.map((y) => (
            <MenuItem key={y} value={String(y)}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}
